/**
 * The public demo: the clinic app, answered by an engine in this tab
 * (runtime.ts). Served at /demo/ beside the real app; it never calls the API.
 */
import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nProvider, useI18n } from '../i18n';
import { App } from '../App';
import { DemoRuntime, DEMO_LOGIN, type DemoResponse } from './runtime';
import '@fontsource-variable/montserrat/wght.css';
import '@fontsource/almarai/400.css';
import '@fontsource/almarai/700.css';
import '@fontsource/almarai/800.css';
import '../styles.css';
import './demo.css';

const BASE = '/demo/';
const runtime = new DemoRuntime(BASE);
const locale = (): 'en' | 'ar' => (document.documentElement.lang === 'ar' ? 'ar' : 'en');

function toResponse(r: DemoResponse): Response {
  return new Response(r.status === 204 ? null : (r.body as BodyInit | null), { status: r.status, headers: { 'Content-Type': r.type } });
}

// Every /api call of the app comes here instead of the network.
const networkFetch = window.fetch.bind(window);
window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const href = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  const url = new URL(href, window.location.href);
  if (url.origin !== window.location.origin || !url.pathname.startsWith('/api/')) return networkFetch(input, init);
  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
  const raw = init?.body ?? (input instanceof Request && method !== 'GET' ? await input.clone().text() : null);
  const body = raw == null ? null : typeof raw === 'string' ? raw : await new Response(raw as BodyInit).text();
  return toResponse(await runtime.handle(method, url, body, locale()));
};

window.oxyDemo = {
  login: { ...DEMO_LOGIN },
  reset: () => {
    runtime.reset();
    window.location.reload();
  },
  changes: () => runtime.changeCount,
};

/**
 * An <img> or a link pointing at /api never goes through fetch: each one is
 * answered here and pointed at an in-memory copy (a blob: URL) instead.
 */
const localCopies = new Map<string, Promise<string | null>>();
function localCopy(path: string): Promise<string | null> {
  let p = localCopies.get(path);
  if (!p) {
    p = runtime.handle('GET', new URL(path, window.location.href), null, locale()).then((r) => {
      if (r.status !== 200 || r.body == null) return null;
      const blob = r.body instanceof Blob ? r.body : new Blob([r.body as BlobPart], { type: r.type });
      return URL.createObjectURL(blob);
    });
    localCopies.set(path, p);
  }
  return p;
}
function localize(el: Element) {
  const attr = el instanceof HTMLImageElement ? 'src' : el instanceof HTMLAnchorElement ? 'href' : null;
  const value = attr ? el.getAttribute(attr) : null;
  if (!attr || !value?.startsWith('/api/')) return;
  void localCopy(value).then((url) => {
    if (url && el.getAttribute(attr) === value) el.setAttribute(attr, url);
  });
}
function watchMedia() {
  const scan = (root: ParentNode) => root.querySelectorAll('img[src^="/api/"], a[href^="/api/"]').forEach(localize);
  new MutationObserver((changes) => {
    for (const c of changes) {
      if (c.type === 'attributes') localize(c.target as Element);
      else c.addedNodes.forEach((n) => n instanceof Element && (localize(n), scan(n)));
    }
  }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'href'] });
  scan(document);
}

function DemoBar() {
  const { locale: lang } = useI18n();
  const [changes, setChanges] = useState(runtime.changeCount);
  useEffect(() => {
    const id = window.setInterval(() => setChanges(runtime.changeCount), 1500);
    return () => window.clearInterval(id);
  }, []);
  const ar = lang === 'ar';
  return (
    <div className="demo-bar" role="note">
      <span className="demo-dot" aria-hidden="true" />
      <strong>{ar ? 'نسخة تجريبية' : 'Live demo'}</strong>
      <span className="demo-note">{ar ? 'كل ما تفعله يبقى في هذه النافذة فقط، ولا يُحفظ على خوادمنا.' : 'Everything you do stays in this tab. Nothing is saved on our servers.'}</span>
      {changes > 0 ? (
        <button type="button" className="demo-reset" onClick={() => window.oxyDemo?.reset()}>
          {ar ? 'إعادة البيانات' : 'Reset the data'}
        </button>
      ) : null}
      <a className="demo-site" href="https://oxyflows.com/">
        oxyflows.com
      </a>
    </div>
  );
}

function Failed({ message }: { message: string }) {
  return (
    <div className="demo-failed">
      <p>{message}</p>
      <button type="button" className="btn" onClick={() => window.location.reload()}>
        Try again
      </button>
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
watchMedia();
runtime
  .load()
  .then(() =>
    root.render(
      <StrictMode>
        <I18nProvider>
          <App />
          <DemoBar />
        </I18nProvider>
      </StrictMode>,
    ),
  )
  .catch((e: unknown) => root.render(<Failed message={e instanceof Error ? e.message : 'The demo could not start.'} />));
